// src/pages/financeiro/RecebimentoNotasModal.js
import React, { useEffect, useState } from 'react';
import Modal from '../../components/modalForm/StackedBootstrapModal';
import { buscarNotasRecebimento } from '../../services/financeiro/RecebimentoService.js';
import { formatCurrency, formatNumeric } from '../../utils/numberUtils.js';
import { formatDate, formatTime } from '../../utils/dateTimeUtils.js';
import { getDescricaoSituacaoNF } from '../../models/vendas/SituacaoNF.ts';
import './RecebimentoNotasModal.css';

const obterTituloNota = (nota, indice) => {
    const modelo = nota.modeloNFDesc && nota.modeloNFDesc !== 'Desconhecido'
        ? nota.modeloNFDesc
        : 'Nota';
    const identificador = nota.numDoc || nota.id;
    return `${modelo} ${identificador} · Documento ${indice + 1}`;
};

const RecebimentoNotasModal = ({ show, recebimentoId, onHide }) => {
    const [notas, setNotas] = useState([]);
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');

    useEffect(() => {
        if (!show || !recebimentoId) return;

        const carregarNotas = async () => {
            setCarregando(true);
            setErro('');
            try {
                const resultado = await buscarNotasRecebimento(recebimentoId);
                setNotas(resultado || []);
            } catch (error) {
                setNotas([]);
                setErro(error?.message || 'Não foi possível carregar as notas vinculadas.');
            } finally {
                setCarregando(false);
            }
        };

        carregarNotas();
    }, [show, recebimentoId]);

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            centered
            scrollable
            dialogClassName="recebimento-notas-modal"
        >
            <Modal.Header closeButton className="recebimento-notas-modal__header">
                <div>
                    <Modal.Title>Notas do recebimento #{recebimentoId}</Modal.Title>
                    <small>Documentos fiscais e itens vinculados à conta a receber</small>
                </div>
            </Modal.Header>
            <Modal.Body className="recebimento-notas-modal__body">
                {carregando && (
                    <div className="recebimento-notas-modal__estado">
                        <div className="spinner-border text-primary" role="status" />
                        <span>Carregando notas...</span>
                    </div>
                )}

                {!carregando && erro && (
                    <div className="alert alert-danger mb-0">{erro}</div>
                )}

                {!carregando && !erro && notas.length === 0 && (
                    <div className="recebimento-notas-modal__estado">
                        Nenhuma nota vinculada a este recebimento.
                    </div>
                )}

                {!carregando && !erro && notas.map((nota, indice) => (
                    <section className="recebimento-nota-card" key={nota.id}>
                        <header className="recebimento-nota-card__titulo">
                            <div>
                                <span className="recebimento-nota-card__sequencia">{indice + 1}</span>
                                <strong>{obterTituloNota(nota, indice)}</strong>
                            </div>
                            <span className="recebimento-nota-card__valor">
                                {formatCurrency(nota.vNota)}
                            </span>
                        </header>

                        <div className="recebimento-nota-card__dados">
                            <div><span>ID</span><strong>{nota.id}</strong></div>
                            <div><span>Série</span><strong>{nota.serie || '-'}</strong></div>
                            <div><span>Número</span><strong>{nota.numDoc || nota.id}</strong></div>
                            <div><span>Modelo</span><strong>{nota.modeloNFDesc || nota.modeloNF || '-'}</strong></div>
                            <div><span>Emissão</span><strong>{formatDate(nota.dataLanc)} {formatTime(nota.dataLanc)}</strong></div>
                            <div><span>Situação</span><strong>{getDescricaoSituacaoNF(nota.situacaoNF)}</strong></div>
                            <div className="recebimento-nota-card__cliente">
                                <span>Cliente</span>
                                <strong>{nota.clienteNome || nota.cliente?.pessoa?.nome || 'Não informado'}</strong>
                            </div>
                        </div>

                        <div className="recebimento-nota-card__itens">
                            <h6>Itens da nota</h6>
                            <div className="table-responsive">
                                <table className="table table-sm table-hover align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th className="text-end">Item</th>
                                            <th>Descrição</th>
                                            <th className="text-end">Quantidade</th>
                                            <th className="text-end">Valor unitário</th>
                                            <th className="text-end">Valor total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {nota.notaItens?.length > 0 ? nota.notaItens.map(item => (
                                            <tr key={item.id}>
                                                <td className="text-end">{item.item}</td>
                                                <td>{item.xProd || item.produto?.descricao || '-'}</td>
                                                <td className="text-end">{formatNumeric(item.qtdCom)}</td>
                                                <td className="text-end">{formatCurrency(item.vUnCom)}</td>
                                                <td className="text-end fw-semibold">{formatCurrency(item.vProd)}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted py-3">
                                                    Nenhum item encontrado nesta nota.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>
                ))}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={onHide}>
                    Fechar
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default RecebimentoNotasModal;
